import { Component, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { UnifiedActivity, ActivityType } from './unified-activity.model';

export interface ActivityDayDialogData {
  date: Date;
  activities: UnifiedActivity[];
}

export interface ActivityDayDialogResult {
  action: 'view-details';
  activity: UnifiedActivity;
}

@Component({
  selector: 'app-activity-day-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, DatePipe],
  template: `
    <h2 mat-dialog-title>{{ data.date | date:'EEEE, MMM d' }}</h2>

    <mat-dialog-content>
      @if (!data.activities.length) {
        <p class="empty-state">Nothing scheduled for this day.</p>
      }

      @for (activity of data.activities; track activity.id) {
        <div class="activity-row">
          <div class="icon-badge" [ngClass]="activity.type">
            <mat-icon>{{ iconFor(activity.type) }}</mat-icon>
          </div>

          <div class="info">
            <div class="title">{{ activity.title }}</div>
            <div class="time">
              {{ activity.start | date:'shortTime' }}
              @if (activity.end) { – {{ activity.end | date:'shortTime' }} }
              @if (activity.location) { · {{ activity.location }} }
            </div>
          </div>

          <div class="actions">
            @if (activity.type === 'event') {
              @if (activity.meetingUrl) {
                <button mat-stroked-button color="primary" (click)="join(activity)">Join</button>
              } @else if (activity.registrationUrl) {
                <button mat-stroked-button (click)="register(activity)">Register</button>
              }
            } @else {
              @if (activity.isVirtual && activity.meetingUrl) {
                <button mat-stroked-button color="primary" (click)="join(activity)">Join</button>
              }
              <button mat-icon-button (click)="viewDetails(activity)" aria-label="View details">
                <mat-icon>info</mat-icon>
              </button>
            }
          </div>
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close()">Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { min-width: 320px; }
    .empty-state { color: var(--mat-sys-on-surface-variant); padding: 12px 0; }
    .activity-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 0;
      border-top: 1px solid var(--mat-sys-outline-variant);
    }
    .activity-row:first-of-type { border-top: none; }
    .icon-badge {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .icon-badge.appointment { background: #E6F1FB; color: #0C447C; }
    .icon-badge.telemedicine { background: #E1F5EE; color: #085041; }
    .icon-badge.event { background: #FAECE7; color: #712B13; }
    .info { flex: 1; min-width: 0; }
    .title { font-size: 14px; font-weight: 500; }
    .time { font-size: 12px; color: var(--mat-sys-on-surface-variant); margin-top: 2px; }
    .actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
  `]
})
export class ActivityDayDialogComponent {
  dialogRef = inject(MatDialogRef<ActivityDayDialogComponent, ActivityDayDialogResult | void>);
  data: ActivityDayDialogData = inject(MAT_DIALOG_DATA);

  iconFor(type: ActivityType): string {
    switch (type) {
      case 'telemedicine': return 'videocam';
      case 'event': return 'groups';
      default: return 'calendar_today';
    }
  }

  join(activity: UnifiedActivity): void {
    if (activity.meetingUrl) {
      window.open(activity.meetingUrl, '_blank', 'noopener,noreferrer');
    }
  }

  register(activity: UnifiedActivity): void {
    if (activity.registrationUrl) {
      window.open(activity.registrationUrl, '_blank', 'noopener,noreferrer');
    }
  }

  viewDetails(activity: UnifiedActivity): void {
    // Bubble the pick back to whoever opened this dialog (dashboard widget or
    // calendar page) so it can open the existing AppointmentDetailsComponent /
    // EventDetailsDialogComponent with the raw resource.
    this.dialogRef.close({ action: 'view-details', activity });
  }
}
